import React, { useEffect } from 'react';
import type { FormFieldProps } from 'semantic-ui-react';
import type { GdprApprovedFieldType } from '@axiom/const';
import { GdprApprovedFields } from '@axiom/const';

import { DesignSysEnvUtil } from '../../../utils/design-sys-env-util';
import { LoggerUtil } from '../../../utils/logger-util';
import { FormField } from '../FormField/FormField';
import { useFormContext } from '../Form/useFormContext';

import type { RawTextareaProps } from './RawTextarea';
import { RawTextarea } from './RawTextarea';

export type TextareaProps = {
  description?: string;
  disabled?: RawTextareaProps['disabled'];
  enableSuggestionsFor?: GdprApprovedFieldType;
  formatting?: RawTextareaProps['formatting'];
  full?: boolean;
  id?: RawTextareaProps['id'];
  label?: React.ReactNode;
  name: RawTextareaProps['name'];
  onBlur?: () => void;
  onChange?: RawTextareaProps['onChange'];
  onFocus?: () => void;
  placeholder?: RawTextareaProps['placeholder'];
  readonly?: boolean;
  /**
   * Makes Enter submit the surrounding form, leaving Shift+Enter to add a line.
   *
   * Off by default, because Enter inserting a line is what a textarea does and
   * what every existing consumer expects. Worth turning on where the field is a
   * single-purpose composer - a chat prompt, a comment box - and wrong where it
   * is one field among several, since Enter would submit the lot.
   *
   * Enter on an empty field still submits, and validation reports it as usual.
   */
  submitOnEnter?: boolean;
  Tooltip?: React.ReactNode;
  variation?: RawTextareaProps['variation'];
};
const noopFn = () => {};

export const Textarea = ({
  description,
  disabled,
  enableSuggestionsFor,
  formatting,
  full,
  id,
  label,
  name,
  onBlur = noopFn,
  onChange = noopFn,
  onFocus = noopFn,
  placeholder,
  readonly,
  submitOnEnter = false,
  Tooltip,
  variation,
}: TextareaProps) => {
  const resolvedId = id || name;
  /*
    The form's own submit, the same function its submit button calls, so an
    Enter goes through Formik's validation and `isSubmitting` exactly as a click
    does. Read unconditionally - the hook is cheap and the context's default
    only throws if `fireSubmit` is actually called outside a form.
  */
  const { fireSubmit } = useFormContext();

  // Requires Sapling to be initialized in target application (SaplingUtil.initialize())
  useEffect(() => {
    if (DesignSysEnvUtil.enableSuggestions && enableSuggestionsFor) {
      if (!GdprApprovedFields.includes(enableSuggestionsFor)) {
        throw new Error(
          `Suggestions are not allowed for ${enableSuggestionsFor}`
        );
      }

      const SaplingModule = import('@saplingai/sapling-js/observer');

      /**
       * Sapling observes the contenteditable element itself, which RawTextarea
       * marks with a class name it owns rather than an editor-library one.
       *
       * `CSS.escape` is what makes the lookup safe: `resolvedId` comes from the
       * field's `name`, and interpolating one that is not also a valid CSS id
       * selector — an array-indexed form field such as `positions[0].description`
       * is enough — makes `querySelector` throw a `SyntaxError`. This runs
       * outside the `try` below, so that would take the whole field down rather
       * than degrade to "no suggestions".
       */
      const editor = document
        .querySelector(`#${CSS.escape(resolvedId)}`)
        ?.querySelector<HTMLElement>('.raw-textarea-editor');

      void SaplingModule.then(({ Sapling }) => {
        try {
          if (editor) {
            Sapling.observe(editor);
          }
        } catch (e) {
          LoggerUtil.fireError(
            `There was an error with Sapling. Did you initialize within the application? Error: ${JSON.stringify(e, null, 2)}`
          );
        }
      });

      return () => {
        void SaplingModule.then(({ Sapling }) => {
          if (editor) {
            Sapling.unobserve(editor);
          }
        });
      };
    }

    return () => {};
  }, [enableSuggestionsFor, resolvedId]);

  return (
    <FormField
      description={description}
      disabled={disabled}
      id={resolvedId}
      name={name}
      label={label}
      showCharactersRemaining
      Tooltip={Tooltip}
      renderField={({
        value,
        onBlur: formOnBlur,
        setFieldTouched,
        setFieldValue,
        invalid,
      }: {
        value: RawTextareaProps['value'];
      } & FormFieldProps) => (
        <RawTextarea
          disabled={disabled}
          formatting={formatting}
          full={full}
          id={resolvedId}
          invalid={invalid}
          name={name}
          onBlur={() => {
            onBlur();
            formOnBlur(name);
            setFieldTouched(name, true);
          }}
          onChange={v => {
            void onChange(v);
            setFieldValue(name, v);
          }}
          onEnter={
            submitOnEnter
              ? () => {
                  // Wrapped rather than passed: `fireSubmit` may hand back a
                  // promise, and `onEnter` returns void.
                  void fireSubmit();
                }
              : undefined
          }
          onFocus={() => {
            onFocus();
          }}
          placeholder={placeholder}
          readonly={readonly}
          value={value}
          variation={variation}
        />
      )}
    />
  );
};
